import Phaser from 'phaser';
import { Entity } from '../entities/base/Entity';
import { WalkerBehavior } from '../entities/behaviors/WalkerBehavior';
import { ShooterBehavior } from '../entities/behaviors/ShooterBehavior';
import { TransformBehavior } from '../entities/behaviors/TransformBehavior';
import { Behavior } from '../entities/behaviors/Behavior';
import { LayerDepths } from '../constants/enums';

/**
 * RangeDebugOverlay - Visualiza los rangos de detección y huida
 *
 * Toggle con SHIFT+F2 para mostrar/ocultar:
 * - Flee ranges (magenta punteado)
 * - Shooter detection ranges (amarillo)
 * - Chase ranges (verde-cyan)
 * - Líneas de disparo activas (naranja-rojo)
 */

const COLORS = {
  fleeRange: 0xff66ff,    // Magenta/rosa para flee range
  shooterRange: 0xffff00, // Amarillo para shooter detection range
  shooterActive: 0xff4400,// Naranja-rojo cuando está disparando
  chaseRange: 0x00ff88,   // Verde-cyan para chase range
  transformRange: 0x00ffff, // Cyan para transform detection range
  text: '#ffff00',
};

export class RangeDebugOverlay {
  private scene: Phaser.Scene;
  private graphics: Phaser.GameObjects.Graphics;
  private enabled: boolean = false;
  private statusText: Phaser.GameObjects.Text;

  // Referencia al player para excluir
  private player?: Phaser.Physics.Arcade.Sprite;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Crear graphics para dibujar rangos
    this.graphics = scene.add.graphics();
    this.graphics.setDepth(LayerDepths.Debug);
    this.graphics.setVisible(false);

    // Crear texto de estado
    this.statusText = scene.add.text(16, scene.scale.height - 70, '', {
      fontFamily: 'monospace',
      fontSize: '18px',
      color: COLORS.text,
      backgroundColor: '#000000cc',
      padding: { x: 10, y: 6 },
    });
    this.statusText.setScrollFactor(0);
    this.statusText.setDepth(LayerDepths.DebugGraphics);
    this.statusText.setVisible(false);

    // Registrar tecla SHIFT+F2
    scene.input.keyboard!.on('keydown-F2', (event: KeyboardEvent) => {
      if (event.shiftKey) {
        event.preventDefault();
        this.toggle();
      }
    });
  }

  /**
   * Establece referencia al player para excluir de rangos
   */
  setPlayer(player: Phaser.Physics.Arcade.Sprite): void {
    this.player = player;
  }

  /**
   * Toggle visibilidad del overlay
   */
  toggle(): void {
    this.enabled = !this.enabled;
    this.graphics.setVisible(this.enabled);
    this.statusText.setVisible(this.enabled);

    if (this.enabled) {
      console.log('[Debug] Range overlay ENABLED - Press SHIFT+F2 to hide');
    } else {
      console.log('[Debug] Range overlay DISABLED');
      this.graphics.clear();
    }
  }

  /**
   * Update - llamar cada frame para redibujar
   */
  update(): void {
    if (!this.enabled) return;

    // Limpiar gráficos anteriores
    this.graphics.clear();

    let fleeCount = 0;
    let shooterCount = 0;
    let transformCount = 0;

    // Dibujar flee ranges
    fleeCount = this.drawFleeRanges();

    // Dibujar shooter ranges
    shooterCount = this.drawShooterRanges();

    // Dibujar transform ranges
    transformCount = this.drawTransformRanges();

    // Actualizar texto de estado
    this.statusText.setText(
      `[RANGE DEBUG] SHIFT+F2 to hide | Flee: ${fleeCount} | Shooter: ${shooterCount} | Transform: ${transformCount}`
    );
  }

  /**
   * Dibuja círculos de flee range para entidades con canFlee habilitado
   */
  private drawFleeRanges(): number {
    const bodies = this.scene.physics.world.bodies;
    let count = 0;

    bodies.entries.forEach((body: Phaser.Physics.Arcade.Body) => {
      const gameObject = body.gameObject;
      if (!gameObject || !body.enable) return;

      // Solo entidades (no el player, no proyectiles)
      if (!(gameObject instanceof Entity)) return;
      if (this.player && gameObject === this.player) return;

      // Buscar WalkerBehavior con canFlee
      const entity = gameObject as Entity;
      const walkerBehavior = entity.getBehavior(WalkerBehavior);

      if (!walkerBehavior || !walkerBehavior.canFlee) return;

      count++;
      const fleeRange = walkerBehavior.fleeRange;
      const centerX = entity.x;
      const centerY = entity.y;

      // Dibujar círculo de flee range (línea punteada, más gruesa)
      this.graphics.lineStyle(4, COLORS.fleeRange, 0.7);

      // Simular línea punteada con segmentos
      const segments = 32;
      const dashRatio = 0.6;

      for (let i = 0; i < segments; i++) {
        const startAngle = (i / segments) * Math.PI * 2;
        const endAngle = ((i + dashRatio) / segments) * Math.PI * 2;

        this.graphics.beginPath();
        this.graphics.arc(centerX, centerY, fleeRange, startAngle, endAngle, false);
        this.graphics.strokePath();
      }

      // Etiqueta de flee range
      const isFleeing = walkerBehavior.isFleeing;
      const label = isFleeing ? `FLEEING!` : `flee: ${fleeRange}px`;
      const labelColor = isFleeing ? '#ff0000' : '#ff66ff';

      const textX = centerX;
      const textY = centerY - fleeRange - 15;

      const text = this.scene.add.text(textX, textY, label, {
        fontFamily: 'Arial',
        fontSize: '14px',
        fontStyle: 'bold',
        color: labelColor,
        backgroundColor: '#000000aa',
        padding: { x: 4, y: 2 },
      });
      text.setOrigin(0.5, 1);
      text.setDepth(LayerDepths.DebugText);

      // Destruir en el siguiente frame
      this.scene.time.delayedCall(16, () => text.destroy());
    });

    return count;
  }

  /**
   * Dibuja círculos de shooter range y líneas de disparo/chase
   */
  private drawShooterRanges(): number {
    const bodies = this.scene.physics.world.bodies;
    let count = 0;

    bodies.entries.forEach((body: Phaser.Physics.Arcade.Body) => {
      const gameObject = body.gameObject;
      if (!gameObject || !body.enable) return;

      // Verificar si tiene ShooterBehavior
      if (typeof (gameObject as Entity).getBehavior !== 'function') return;

      const entity = gameObject as Entity;

      // Get ShooterBehavior
      const entityAny = entity as Entity & { behaviors?: Behavior[] };
      const shooterBehavior = entityAny.behaviors?.find(b => b instanceof ShooterBehavior) as ShooterBehavior | undefined;

      if (!shooterBehavior || shooterBehavior.detectionRange <= 0) return;

      count++;
      const detectionRange = shooterBehavior.detectionRange;
      const centerX = entity.x;
      const centerY = entity.y;

      // Color based on state
      const isGuarding = (shooterBehavior as ShooterBehavior & { isGuarding?: boolean }).isGuarding;
      const isChasing = shooterBehavior.chaseWhileShooting;

      let rangeColor = COLORS.shooterRange;
      let label = `detect: ${detectionRange}px`;

      if (isGuarding) {
        rangeColor = COLORS.shooterActive;
        label = `GUARDING`;
      } else if (isChasing) {
        rangeColor = COLORS.chaseRange;
        label = `chase: ${detectionRange}px`;
      }

      // Dibujar círculo de detection range
      this.graphics.lineStyle(4, rangeColor, 0.6);
      this.graphics.strokeCircle(centerX, centerY, detectionRange);

      // Dibujar línea hacia el target si está activo
      const currentTarget = (shooterBehavior as ShooterBehavior & { currentTarget?: Entity | null }).currentTarget;
      if (currentTarget && currentTarget.active) {
        this.graphics.lineStyle(3, COLORS.shooterActive, 0.8);
        this.graphics.lineBetween(centerX, centerY, currentTarget.x, currentTarget.y);
        label = isChasing ? `CHASING!` : `SHOOTING!`;
      }

      // Dibujar min range si existe
      if (shooterBehavior.minRange > 0) {
        this.graphics.lineStyle(2, 0x00ff00, 0.4);
        this.graphics.strokeCircle(centerX, centerY, shooterBehavior.minRange);
      }

      // Etiqueta
      const labelColor = currentTarget ? '#ff4400' : (isChasing ? '#00ff88' : '#ffff00');
      const textX = centerX;
      const textY = centerY - detectionRange - 15;

      const text = this.scene.add.text(textX, textY, label, {
        fontFamily: 'Arial',
        fontSize: '14px',
        fontStyle: 'bold',
        color: labelColor,
        backgroundColor: '#000000aa',
        padding: { x: 4, y: 2 },
      });
      text.setOrigin(0.5, 1);
      text.setDepth(LayerDepths.DebugText);

      // Destruir en el siguiente frame
      this.scene.time.delayedCall(16, () => text.destroy());
    });

    return count;
  }

  /**
   * Dibuja círculos de transform range para entidades con TransformBehavior
   * Solo muestra entidades que AÚN NO se han transformado
   */
  private drawTransformRanges(): number {
    const bodies = this.scene.physics.world.bodies;
    let count = 0;

    bodies.entries.forEach((body: Phaser.Physics.Arcade.Body) => {
      const gameObject = body.gameObject;
      if (!gameObject || !body.enable) return;

      // Verificar si tiene getBehavior
      if (typeof (gameObject as Entity).getBehavior !== 'function') return;

      const entity = gameObject as Entity;

      // Get TransformBehavior
      const transformBehavior = entity.getBehavior(TransformBehavior);

      // Skip si no tiene behavior, si ya se transformó, o si detectionRange es 0
      if (!transformBehavior) return;
      if (transformBehavior.hasCompletedTransform()) return;
      if (transformBehavior.detectionRange <= 0) return;

      count++;
      const detectionRange = transformBehavior.detectionRange;
      const centerX = entity.x;
      const centerY = entity.y;

      // Dibujar círculo punteado cyan
      this.graphics.lineStyle(4, COLORS.transformRange, 0.7);

      // Simular línea punteada con segmentos (igual que fleeRange)
      const segments = 32;
      const dashRatio = 0.6;

      for (let i = 0; i < segments; i++) {
        const startAngle = (i / segments) * Math.PI * 2;
        const endAngle = ((i + dashRatio) / segments) * Math.PI * 2;

        this.graphics.beginPath();
        this.graphics.arc(centerX, centerY, detectionRange, startAngle, endAngle, false);
        this.graphics.strokePath();
      }

      // Etiqueta
      const label = `transform: ${detectionRange}px`;
      const textX = centerX;
      const textY = centerY - detectionRange - 15;

      const text = this.scene.add.text(textX, textY, label, {
        fontFamily: 'Arial',
        fontSize: '14px',
        fontStyle: 'bold',
        color: '#00ffff',
        backgroundColor: '#000000aa',
        padding: { x: 4, y: 2 },
      });
      text.setOrigin(0.5, 1);
      text.setDepth(LayerDepths.DebugText);

      // Destruir en el siguiente frame
      this.scene.time.delayedCall(16, () => text.destroy());
    });

    return count;
  }

  /**
   * Destruir el overlay
   */
  destroy(): void {
    this.graphics.destroy();
    this.statusText.destroy();
  }
}
